<template>
  <SocialMentionCard
    v-bind="{ ...$props, ...$attrs }"
    ref="card"
    class="pointer"
    :icon="socialIcon('instagram_post')"
    :list-title="listTitle"
    :title="mention.title"
    @click="mentionClicked($event)"
    @mention-removed="removeMention"
    @mention-selected="$emit('mention-selected', $event)"
  >
    <template #header>
      <span class="title">
        <SocialSyndicationsPopper
          v-if="mention.author"
          :mention="mention"
          :parent-el-ref="$refs.card?.$el"
          :stream="stream"
          @syndication-clicked="mentionClicked($event)"
        >
          <template #default="{ toggleOpen }">
            <BaseButton
              v-if="hasSyndications"
              class="tw:gap-0! tw:font-bold"
              new-design
              plain
              @click.stop="toggleOpen"
            >
              <AuthorWidget
                disable-click
                :format-author="formatAuthor"
                :mention="mention"
              />
              <span class="tw:text-sm tw:text-denim-900">
                +{{ mention.syndications.length }}
              </span>
            </BaseButton>
            <AuthorWidget
              v-else
              class="tw:font-bold"
              :disable-click="options.disableAuthorClick"
              :format-author="formatAuthor"
              :mention="mention"
              no-spacer
            />
            <Bullet v-if="isHashtag" />
          </template>
        </SocialSyndicationsPopper>

        <QIcon
          v-if="mention.author_verified"
          class="text-social-verified social-verified"
          name="ion-checkmark-circle"
          size="14px"
        />

        <span
          v-if="handle && !isHashtag"
          class="text-blue-grey subtitle"
        >
          {{ handle }}
          <Bullet />
        </span>

        <template v-if="mention.author_follower_count">
          <SocialMentionCardFollowerCount
            v-if="mention.author_follower_count"
            display-separator
            :follower-count="mention.author_follower_count"
          />
        </template>
        <TimeAgo
          v-if="mention.timestamp"
          class="text-blue-grey subtitle tw:inline-block tw:items-start"
          :date="mention.timestamp"
        />
      </span>
    </template>

    <slot />

    <template
      v-if="hasAttachments"
      #attachments
    >
      <SocialMentionCardAttachedMedia
        :assets="videos"
        :mention="mention"
        play-media-in-lightbox
      />

      <SocialMentionCardAttachedMedia
        :assets="photos"
        :mention="mention"
        play-media-in-lightbox
      />
    </template>

    <template #engagement-items>
      <div class="tw:flex-1 tw:flex-col tw:text-left tw:text-denim-600">
        <IsPlatform cision-one>
          <BaseIcon
            class="tw:text-xs"
            data-testid="engagement-item-icon"
            :icon="isHashtag ? 'hashtag' : 'grid'"
          />

          <span class="tw:text-xs">
            {{ productType }}
          </span>
        </IsPlatform>
      </div>

      <SocialMentionEngagementItem
        icon="likes"
        :model-value="mention.like_count"
      />

      <SocialMentionEngagementItem
        icon="comments"
        :model-value="mention.comment_count"
      />
    </template>
  </SocialMentionCard>
</template>

<script>
import AuthorWidget from "shared/components/AuthorWidget.vue";
import {
  BaseButton,
  BaseIcon,
  Bullet,
  IsPlatform,
} from "shared/components/base";
import TimeAgo from "shared/components/core/various/TimeAgo.vue";
import { propsExcept, SocialMentionCard } from "shared/components/MentionCard";
import SocialMentionCardAttachedMedia from "shared/components/SocialMentionCardAttachedMedia.vue";
import SocialMentionCardFollowerCount from "shared/components/SocialMentionCardFollowerCount.vue";
import SocialMentionEngagementItem from "shared/components/SocialMentionEngagementItem.vue";
import { SocialSyndicationsPopper } from "shared/components/SyndicationsPopper";
import useMention from "shared/composables/useMention";
import { useMentionAttachments } from "shared/composables/useMentionAttachments";
import { SOCIAL_PREFIX } from "shared/constants";
import { socialIcon } from "shared/helpers/media";
import { ensurePrefix } from "shared/helpers/string";

export default {
  name: "SocialMentionCardInstagramPost",
  components: {
    AuthorWidget,
    BaseButton,
    BaseIcon,
    Bullet,
    IsPlatform,
    SocialMentionCard,
    SocialMentionCardAttachedMedia,
    SocialMentionCardFollowerCount,
    SocialMentionEngagementItem,
    SocialSyndicationsPopper,
    TimeAgo,
  },
  props: {
    ...propsExcept(["listTitle"]),
  },
  emits: ["click", "mention-selected", "mention-removed"],
  setup(props, context) {
    const { mentionClicked } = useMention(props, context);

    const { attachments, photos, videos, hasAttachments } =
      useMentionAttachments(props.mention);

    return {
      attachments,
      hasAttachments,
      mentionClicked,
      photos,
      videos,
    };
  },
  computed: {
    author() {
      return this.mention.author || this.mention.username;
    },
    listTitle() {
      return this.mention.handle || this.mention.username;
    },
    isHashtag() {
      return this.mention.product_type === "hashtag";
    },
    handle() {
      const prefix = this.isHashtag
        ? SOCIAL_PREFIX.hashtag
        : SOCIAL_PREFIX.handle;

      return this.mention.handle
        ? ensurePrefix(this.mention.handle, prefix)
        : "";
    },
    hasSyndications() {
      return this.mention.syndications?.length && this.stream.id;
    },
    productType() {
      return this.isHashtag
        ? this.$t("social_mention_card_instagram.hashtag")
        : this.$t("social_mention_card_instagram.post");
    },
    formatAuthor() {
      return this.isHashtag
        ? (title) => ensurePrefix(title, SOCIAL_PREFIX.hashtag)
        : undefined;
    },
  },
  methods: {
    socialIcon,
    removeMention(mention) {
      this.$emit("mention-removed", mention);
    },
  },
};
</script>

<style lang="scss" scoped>
.social-icon {
  &.ion-checkmark-circle {
    color: rgb(73 208 103 / 50%);
  }

  &.ion-close-circle {
    color: rgb(249 88 88 / 50%);
  }

  &.ion-remove-circle {
    color: rgb(50 159 236 / 50%);
  }
}

.q-icon {
  vertical-align: 0;
}

.social-verified {
  margin: 0 5px 0 2px;
}
</style>
